/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, inject, Injector, Input, OnInit} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService} from '@taiga-ui/core';
import {AddressFormComponent} from './address-form/address-form.component';
import {BusinessFormComponent} from './business-form/business-form.component';
import {BusinessProfileFormComponent} from './business-profile-form/business-profile-form.component';
import {DocumentsFormComponent} from './documents-form/documents-form.component';
import {EstimatedVolumesFormComponent} from './estimated-volumes-form/estimated-volumes-form.component';
import {LeadFormComponent} from './lead-form/lead-form.component';
import {PartnersFormComponent} from './partners-form/partners-form.component';
import {
  ComplianceRequirements,
  DomainPageSettings,
  DomainStyleSettings,
  DomainSettingKey,
} from '@gatewee/types/domain-setting';
import {Onboarding, PartnerRole} from '@gatewee/types/onboarding';
import {CamelcasePipe} from '@gatewee/helpers/pipes/camel-case.pipe';
import {validateCPFOrCNPJ} from '@gatewee/utils/validators/cpf-cnpjs-validators';
import {fullNameValidator} from '@gatewee/utils/validators/fullname.validator';
import {AuthService} from '../../../_services/auth/auth.service';
import {BrandingService} from '@gatewee/services/core/branding.service';
import {PublicService} from '../../../_services/public/public.service';
import {SettingsService} from '@gatewee/services/core/settings.service';
import {RecipientOfficialData} from '@gatewee/types/recipient';
import {
  areAllRequiredDocumentsUploaded,
  buildOnboardingPayload,
  buildProfileFormArrays,
  computeResumeStepIndex,
  hydrateDocumentsFormArray,
  isStepValid,
  markStepTouched,
  type OnboardingFlowForms,
} from '../../../../utils/onboarding-flow.util';

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.css'],
  standalone: false,
})
export class OnboardingComponent implements OnInit {
  private branding = inject(BrandingService);
  private appSettings = inject(SettingsService);

  @Input() identifier!: string;

  partialData?: Onboarding;

  leadFormGroup!: FormGroup;
  businessFormGroup!: FormGroup;
  addressFormGroup!: FormGroup;
  estimatedVolumesFormGroup!: FormGroup;
  businessProfileFormGroup!: FormGroup;
  documentsFormGroup!: FormArray;
  partnersFormGroup!: FormArray;

  allowNaturalPerson = false;

  allowLegalPerson = false;
  selectedStepIndex = 0;

  isLoading = false;
  isSubmitting = false;
  loadError = false;

  steps: {
    component: any;
    formGroup: FormGroup | FormArray | null;
    inputs: {[key: string]: any};
    key: string;
    outputs?: {[key: string]: (data: any) => void};
  }[] = [];

  requiredDocuments: ComplianceRequirements = {
    business_contract: true,
  };

  pageSettings!: DomainPageSettings;
  style!: DomainStyleSettings;

  /** Hero side panel is shown only when a background image URL is configured. */
  get hasOnboardingSidePanelImage(): boolean {
    const url = this.pageSettings?.onboarding?.sidePanel?.backgroundImage;
    return typeof url === 'string' && url.trim().length > 0;
  }

  /** WhatsApp link when configured (absolute URL or digits → wa.me). */
  get supportWhatsappHref(): string | null {
    const raw = this.pageSettings?.supportContactWhatsapp?.trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    const digits = raw.replace(/\D/g, '');
    return digits ? `https://wa.me/${digits}` : null;
  }

  get supportMailtoHref(): string | null {
    const email = this.pageSettings?.supportContactEmail?.trim();
    return email ? `mailto:${email}` : null;
  }

  get hasSupportChannels(): boolean {
    return !!this.supportWhatsappHref || !!this.supportMailtoHref;
  }

  camelcasePipe = new CamelcasePipe();

  constructor(
    private readonly authService: AuthService,
    private readonly publicService: PublicService,
    private readonly router: Router,
    private readonly alerts: TuiAlertService,
    private translate: TranslateService,
    private injector: Injector,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.pageSettings = this.appSettings.get(
      'branding',
      DomainSettingKey.PAGE,
    ) as DomainPageSettings;
    this.style = this.appSettings.get(
      'branding',
      DomainSettingKey.STYLE,
    ) as DomainStyleSettings;

    this.allowLegalPerson = this.appSettings.get(
      'businessRules',
      DomainSettingKey.ALLOW_LEGAL_PERSON_SIGNUP,
    ) as boolean;

    this.allowNaturalPerson = this.appSettings.get(
      'businessRules',
      DomainSettingKey.ALLOW_NATURAL_PERSON_SIGNUP,
    ) as boolean;

    this.requiredDocuments =
      (this.appSettings.get(
        'businessRules',
        DomainSettingKey.COMPLIANCE_REQUIREMENTS,
      ) as ComplianceRequirements) || this.requiredDocuments;

    if (this.authService.isAuthenticated()) {
      void this.router.navigate(['/panel']);
      return;
    }

    if (this.identifier) {
      this.fetchExistingData();
    } else {
      this.buildForms();
    }
  }

  retryFetchExistingData(): void {
    if (this.identifier) {
      this.fetchExistingData();
    }
  }

  private getFlowForms(): OnboardingFlowForms {
    return {
      leadFormGroup: this.leadFormGroup,
      businessFormGroup: this.businessFormGroup,
      addressFormGroup: this.addressFormGroup,
      estimatedVolumesFormGroup: this.estimatedVolumesFormGroup,
      businessProfileFormGroup: this.businessProfileFormGroup,
      partnersFormGroup: this.partnersFormGroup,
      documentsFormGroup: this.documentsFormGroup,
    };
  }

  buildForms() {
    this.leadFormGroup = this.fb.group({
      name: [
        this.partialData?.representative.name ?? '',
        [Validators.required, fullNameValidator],
      ],
      document: [
        this.partialData?.representative.document ?? '',
        [Validators.required, validateCPFOrCNPJ],
      ],
      phone: [
        this.partialData?.representative.phone ?? '',
        Validators.required,
      ],
      email: [
        this.partialData?.representative.email ?? '',
        [Validators.required, Validators.email],
      ],
      acceptTerms: [this.partialData ? true : false, Validators.requiredTrue],
    });

    this.businessFormGroup = this.fb.group({
      document: [
        this.partialData?.document ?? '',
        [Validators.required, validateCPFOrCNPJ],
      ],
      name: [this.partialData?.name ?? '', Validators.required],
      displayName: [this.partialData?.displayName ?? '', Validators.required],
      businessAreas: [this.partialData?.metadata?.businessAreas ?? []],
      officialData: [this.partialData?.officialData ?? {}],
      companyType: [
        this.partialData?.metadata?.companyType ?? '',
        Validators.required,
      ],
    });

    this.addressFormGroup = this.fb.group({
      street: [this.partialData?.address?.street ?? '', Validators.required],
      number: [this.partialData?.address?.number ?? ''],
      complement: [this.partialData?.address?.complement ?? ''],
      neighborhood: [
        this.partialData?.address?.neighborhood ?? '',
        Validators.required,
      ],
      city: [this.partialData?.address?.city ?? '', Validators.required],
      state: [this.partialData?.address?.state ?? '', Validators.required],
      postalCode: [
        this.partialData?.address?.postalCode ?? '',
        Validators.required,
      ],
    });

    const hasWebsite = !!this.partialData?.website?.trim();
    this.estimatedVolumesFormGroup = this.fb.group({
      estimatedMonthlyVolume: [
        this.partialData?.metadata?.estimatedMonthlyVolume ?? '',
        Validators.required,
      ],
      estimatedAverageTicket: [
        this.partialData?.metadata?.estimatedAverageTicket ?? '',
        Validators.required,
      ],
      website: [this.partialData?.website ?? ''],
      noWebsite: [!hasWebsite && !!this.partialData],
    });

    const profileArrays = buildProfileFormArrays(
      this.fb,
      this.partialData?.metadata,
    );
    this.businessProfileFormGroup = this.fb.group({
      clientType: [
        this.partialData?.metadata?.clientType ?? '',
        Validators.required,
      ],
      operatingRegions: profileArrays.operatingRegions,
      organizationalTools: profileArrays.organizationalTools,
    });

    this.partnersFormGroup = this.fb.array<FormGroup>(
      (this.partialData?.partners ?? []).map(partner =>
        this.fb.group({
          name: [partner.name || '', Validators.required],
          role: [partner.role || '', Validators.required],
          document: [partner.document || '', Validators.required],
          email: [partner.email || '', [Validators.required, Validators.email]],
          phone: [partner.phone || '', Validators.required],
          socialName: [partner.socialName || ''],
          partnerSharePercentage: [
            partner.partnerSharePercentage ?? '',
            [Validators.required, Validators.min(0), Validators.max(100)],
          ],
          motherName: [partner.motherName || ''],
          birthDate: [partner.birthDate || '', Validators.required],
          address: this.fb.group({
            street: [partner.address?.street || '', Validators.required],
            number: [partner.address?.number || ''],
            complement: [partner.address?.complement || ''],
            neighborhood: [
              partner.address?.neighborhood || '',
              Validators.required,
            ],
            city: [partner.address?.city || '', Validators.required],
            state: [partner.address?.state || '', Validators.required],
            postalCode: [
              partner.address?.postalCode || '',
              Validators.required,
            ],
          }),
          politicallyExposedPerson: partner.politicallyExposedPerson ?? false,
        }),
      ),
      Validators.required,
    );

    this.documentsFormGroup = hydrateDocumentsFormArray(
      this.fb,
      this.partialData?.files,
      this.requiredDocuments,
    );

    this.handleBusinessOfficialData();

    this.buildSteps();
  }

  fetchExistingData() {
    this.isLoading = true;
    this.loadError = false;

    this.publicService.findOnboarding(this.identifier).subscribe({
      next: (data: Onboarding) => {
        this.partialData = data;
        this.injectSessionUser(data);
        this.buildForms();
        this.selectedStepIndex = computeResumeStepIndex(
          data,
          this.requiredDocuments,
        );
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.loadError = true;
        this.alerts
          .open(
            this.translate.instant('features.onboarding.toast.submitError'),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
      },
    });
  }

  activeStepTitle(): string {
    const step = this.steps[this.selectedStepIndex];
    if (!step) return '';
    return this.translate.instant(
      'features.onboarding.steps.' + step.key + '.title',
    );
  }

  activeStepDescription(): string {
    const step = this.steps[this.selectedStepIndex];
    if (!step) return '';
    return this.translate.instant(
      'features.onboarding.steps.' + step.key + '.description',
    );
  }

  currentStepControl(): AbstractControl | null {
    const step = this.steps[this.selectedStepIndex];
    return step?.formGroup ?? null;
  }

  isCurrentStepInvalid(): boolean {
    return !isStepValid(
      this.selectedStepIndex,
      this.getFlowForms(),
      this.requiredDocuments,
    );
  }

  isFinishDisabled(): boolean {
    return (
      this.isSubmitting ||
      !isStepValid(6, this.getFlowForms(), this.requiredDocuments)
    );
  }

  areAllRequiredDocumentsUploaded(): boolean {
    return areAllRequiredDocumentsUploaded(
      this.documentsFormGroup,
      this.requiredDocuments,
    );
  }

  goPrevious(): void {
    if (this.isSubmitting) {
      return;
    }
    if (this.selectedStepIndex > 0) {
      this.selectedStepIndex--;
    }
  }

  buildSteps() {
    this.steps = [
      {
        key: 'leadForm',
        component: LeadFormComponent,
        formGroup: this.leadFormGroup,
        inputs: {
          formGroup: this.leadFormGroup,
          privacyPolicy: this.pageSettings?.assets?.privacyPolicy || '',
          allowNaturalPerson: this.allowNaturalPerson,
          allowLegalPerson: this.allowLegalPerson,
        },
      },
      {
        key: 'businessForm',
        component: BusinessFormComponent,
        formGroup: this.businessFormGroup,
        inputs: {
          formGroup: this.businessFormGroup,
        },
      },
      {
        key: 'addressForm',
        component: AddressFormComponent,
        formGroup: this.addressFormGroup,
        inputs: {
          formGroup: this.addressFormGroup,
        },
      },
      {
        key: 'partnersForm',
        component: PartnersFormComponent,
        formGroup: this.partnersFormGroup,
        inputs: {
          formGroup: this.partnersFormGroup,
        },
      },
      {
        key: 'estimatedVolumesForm',
        component: EstimatedVolumesFormComponent,
        formGroup: this.estimatedVolumesFormGroup,
        inputs: {
          formGroup: this.estimatedVolumesFormGroup,
        },
      },
      {
        key: 'businessProfileForm',
        component: BusinessProfileFormComponent,
        formGroup: this.businessProfileFormGroup,
        inputs: {
          formGroup: this.businessProfileFormGroup,
        },
      },
      {
        key: 'documentsForm',
        component: DocumentsFormComponent,
        formGroup: this.documentsFormGroup,
        inputs: {
          formGroup: this.documentsFormGroup,
          requiredDocuments: this.requiredDocuments,
        },
      },
    ];

    return this.steps;
  }

  createStepInjector(step: any): Injector {
    const inputs = {
      ...(step.inputs || {}),
      ...(step.formGroup ? {formGroup: step.formGroup} : {}),
    };
    const resolvedInputs = Object.entries(inputs).map(([key, value]) => ({
      provide: key,
      useValue: value,
    }));

    return Injector.create({
      providers: resolvedInputs,
      parent: this.injector,
    });
  }

  nextStep(targetStepIndex: number) {
    if (this.isSubmitting) {
      return;
    }

    const currentStep = this.selectedStepIndex;

    if (targetStepIndex === 1 && currentStep === 0) {
      markStepTouched(0, this.getFlowForms());
      if (!isStepValid(0, this.getFlowForms(), this.requiredDocuments)) {
        this.showValidationError();
        return;
      }
      this.selectedStepIndex = targetStepIndex;
      return;
    }

    if (targetStepIndex === 2 && !this.partialData) {
      markStepTouched(0, this.getFlowForms());
      markStepTouched(1, this.getFlowForms());
      if (
        !isStepValid(0, this.getFlowForms(), this.requiredDocuments) ||
        !isStepValid(1, this.getFlowForms(), this.requiredDocuments)
      ) {
        this.showValidationError();
        return;
      }
      this.createDraft();
      return;
    }

    if (this.partialData && this.identifier) {
      markStepTouched(currentStep, this.getFlowForms());
      if (
        !isStepValid(currentStep, this.getFlowForms(), this.requiredDocuments)
      ) {
        this.showValidationError();
        return;
      }

      this.saveDraft({
        includeLiveFiles: currentStep === 6,
        targetStepIndex,
      });
    }
  }

  handleBusinessOfficialData() {
    const officialData =
      this.partialData?.officialData ??
      ({} as unknown as RecipientOfficialData);

    if (this.partialData?.officialData) {
      this.addressFormGroup.patchValue({
        street: officialData.address?.street || '',
        number: officialData.address?.number || '',
        complement: officialData.address?.details || '',
        neighborhood: officialData.address?.district || '',
        city: officialData.address?.city || '',
        state: officialData.address?.state || '',
        postalCode: officialData.address.zip || '',
      });

      if (
        !this.partialData?.partners?.length &&
        officialData.company.members.length > 0
      ) {
        officialData.company.members.forEach((member: any) => {
          this.partnersFormGroup.push(
            this.fb.group({
              name: [member.person.name || '', Validators.required],
              role: [
                member.role.id === 49
                  ? PartnerRole.PARTNER
                  : PartnerRole.MANAGNIG_PARTNER || '',
                Validators.required,
              ],
              document: ['', Validators.required],
              email: [
                member.person.email || '',
                [Validators.required, Validators.email],
              ],
              phone: [member.person.phone || '', Validators.required],
              partnerSharePercentage: [
                100 / officialData.company.members.length,
                [Validators.required, Validators.min(0), Validators.max(100)],
              ],
              motherName: [''],
              socialName: [member.person.name || ''],
              birthDate: ['', Validators.required],
              address: this.fb.group({
                street: ['', Validators.required],
                number: [''],
                complement: [''],
                neighborhood: ['', Validators.required],
                city: ['', Validators.required],
                state: ['', Validators.required],
                postalCode: ['', Validators.required],
              }),
              politicallyExposedPerson: false,
            }),
          );
        });
      }
    }
  }

  createDraft() {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    const payload = buildOnboardingPayload(this.getFlowForms());

    this.publicService.createOnboarding(payload).subscribe({
      next: (response: Onboarding) => {
        this.isSubmitting = false;
        if (response.identifier) {
          void this.router.navigate(['/onboarding/' + response.identifier]);
          return;
        }

        this.alerts
          .open(
            this.translate.instant('features.onboarding.toast.submitError'),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
      },
      error: error => {
        this.isSubmitting = false;
        this.alerts
          .open(
            error.error?.message ||
              this.translate.instant('features.onboarding.toast.submitError'),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
      },
    });
  }

  saveDraft(options: {
    includeLiveFiles?: boolean;
    targetStepIndex?: number;
    finish?: boolean;
  }) {
    if (this.isSubmitting || !this.identifier) {
      return;
    }

    if (options.finish) {
      for (let step = 0; step <= 6; step++) {
        markStepTouched(step, this.getFlowForms());
        if (!isStepValid(step, this.getFlowForms(), this.requiredDocuments)) {
          this.showValidationError();
          return;
        }
      }

      if (!this.areAllRequiredDocumentsUploaded()) {
        this.alerts
          .open(
            this.translate.instant(
              'features.onboarding.steps.documentsForm.validation.requiredMissing',
            ),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        return;
      }
    }

    this.isSubmitting = true;
    const payload = buildOnboardingPayload(this.getFlowForms(), {
      partialData: this.partialData,
      includeLiveFiles: options.includeLiveFiles ?? false,
    });

    this.publicService.updateOnboarding(this.identifier, payload).subscribe({
      next: (response: Onboarding) => {
        this.isSubmitting = false;
        this.partialData = response;
        this.injectSessionUser(response);

        if (options.finish) {
          this.alerts
            .open(
              this.translate.instant('features.onboarding.toast.updateSuccess'),
              {
                label: this.translate.instant('common.status.success'),
                appearance: 'success',
              },
            )
            .subscribe();
          void this.router.navigate(['/onboarding-success']);
          return;
        }

        if (options.targetStepIndex !== undefined) {
          this.selectedStepIndex = options.targetStepIndex;
        }
      },
      error: error => {
        this.isSubmitting = false;
        this.alerts
          .open(
            error.error?.message ||
              this.translate.instant('features.onboarding.toast.submitError'),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
      },
    });
  }

  submit() {
    if (!this.partialData || !this.identifier) {
      return;
    }

    this.saveDraft({includeLiveFiles: true, finish: true});
  }

  injectSessionUser(data: Partial<Onboarding>) {
    if (data) {
      return sessionStorage.setItem(
        'sessionUser',
        JSON.stringify({
          id: data.id ?? '',
          name: data.representative?.name,
          email: data.representative?.email,
          companyName: data.name,
          companyDocument: data.document,
          origin: 'onboarding',
        }),
      );
    }
  }

  private showValidationError(): void {
    this.alerts
      .open(
        this.translate.instant('features.onboarding.leadForm.validationError'),
        {
          label: this.translate.instant('common.status.error'),
          appearance: 'error',
        },
      )
      .subscribe();
  }
}
